import { useState } from "react";
import { getStats, getLevel, ACHIEVEMENTS } from "../gamification";
import Card from "../components/atoms/Card";
import Chip from "../components/atoms/Chip";

const DAYS = ["M", "T", "W", "T", "F", "S", "S"];

export default function Progress() {
  const [stats] = useState(getStats);
  const levelInfo = getLevel(stats.totalXp);
  const maxWeekly = Math.max(...stats.weeklyActivity, 1);
  const topErrors = Object.entries(stats.errorTypes || {}).sort((a, b) => b[1] - a[1]).slice(0, 3);
  const unlockedSet = new Set(stats.unlockedAchievements || []);
  const unlocked = ACHIEVEMENTS.filter((a) => unlockedSet.has(a.id));
  const locked = ACHIEVEMENTS.filter((a) => !unlockedSet.has(a.id)).slice(0, 6);

  return (
    <div className="page-enter" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <Chip tone="gold">Progress</Chip>
        <h1 className="font-display" style={{ fontSize: "clamp(40px, 6vw, 56px)", lineHeight: 1.05, fontWeight: 400, letterSpacing: "-0.025em", margin: "12px 0 8px" }}>
          The <em style={{ color: "var(--gold-500)" }}>arc</em> of it.
        </h1>
        <p style={{ color: "var(--fg-2)", fontSize: 15, maxWidth: "56ch", margin: 0 }}>
          Numbers worth checking once a week — not once an hour.
        </p>
      </div>

      {/* Top stats — serif numerals on paper-deep band */}
      <div style={{ background: "var(--paper-deep)", border: "1px solid var(--border-2)", borderRadius: "var(--radius-lg)", padding: 24 }}>
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 24 }}>
          <BigStat value={stats.totalXp} label="Total XP" />
          <BigStat value={`Lv ${levelInfo.level}`} label={levelInfo.name} />
          <BigStat value={stats.streak || 0} label="Day streak" />
          <BigStat value={stats.lessonsCompleted || 0} label="Lessons done" />
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: "1.4fr 1fr", gap: 24 }}>
        {/* Weekly chart */}
        <Card>
          <span className="eyebrow">This week</span>
          <h3 className="font-display" style={{ fontSize: 24, fontWeight: 400, margin: "6px 0 14px" }}>
            Daily XP, last 7 days
          </h3>
          <div style={{ height: 120, position: "relative", borderBottom: "1px solid var(--border-1)", marginBottom: 12 }}>
            <svg viewBox="0 0 280 110" preserveAspectRatio="none" style={{ width: "100%", height: "100%", overflow: "visible" }}>
              <polyline
                points={stats.weeklyActivity.map((m, i) => {
                  const x = (i / 6) * 280;
                  const y = 110 - (m / maxWeekly) * 95;
                  return `${x},${y}`;
                }).join(" ")}
                fill="none"
                stroke="#8aab87"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {stats.weeklyActivity.map((m, i) => {
                const x = (i / 6) * 280;
                const y = 110 - (m / maxWeekly) * 95;
                return <circle key={i} cx={x} cy={y} r="3" fill="#c89a2c" />;
              })}
            </svg>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--fg-3)", fontWeight: 600 }}>
            {DAYS.map((d, i) => <span key={i}>{d}</span>)}
          </div>
        </Card>

        {/* Level progress */}
        <Card paperDeep>
          <span className="eyebrow">Level</span>
          <h3 className="font-display" style={{ fontSize: 36, lineHeight: 1, margin: "6px 0 4px", fontWeight: 400 }}>
            <em style={{ color: "var(--gold-500)" }}>{levelInfo.name}</em>
          </h3>
          <p style={{ fontSize: 13, color: "var(--fg-3)", margin: "0 0 14px" }}>
            {stats.totalXp} / {levelInfo.nextXp} XP
          </p>
          <div style={{ height: 6, background: "rgba(11,17,29,0.10)", borderRadius: 3, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${levelInfo.progress}%`, background: "var(--gold-500)", transition: "width 700ms cubic-bezier(.2,.8,.2,1)" }} />
          </div>
          <p style={{ fontSize: 11, color: "var(--fg-3)", margin: "10px 0 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.12em" }}>
            {levelInfo.nextXp - stats.totalXp} XP to next level
          </p>
        </Card>
      </div>

      <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 24 }}>
        {/* Top errors */}
        <Card>
          <span className="eyebrow">Where you slip</span>
          <h3 className="font-display" style={{ fontSize: 24, fontWeight: 400, margin: "6px 0 14px" }}>Three rough spots</h3>
          {topErrors.length > 0 ? (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {topErrors.map(([type, count]) => (
                <li key={type} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid var(--border-1)", fontSize: 14 }}>
                  <span style={{ color: "var(--fg-1)", fontWeight: 600, textTransform: "capitalize" }}>{type.replace(/_/g, " ")}</span>
                  <span style={{ color: "var(--berry)", fontWeight: 700 }}>{count}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p style={{ color: "var(--fg-3)", fontSize: 14, margin: 0 }}>No common errors yet.</p>
          )}
        </Card>

        {/* Quick stats */}
        <Card>
          <span className="eyebrow">Activity</span>
          <h3 className="font-display" style={{ fontSize: 24, fontWeight: 400, margin: "6px 0 14px" }}>Counted</h3>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {[
              ["Tests passed",        stats.testsPassed || 0],
              ["Speaking sessions",   stats.speakingSessions || 0],
              ["Conversations",       stats.conversationsCompleted || 0],
              ["Words learned",       stats.wordsLearned || 0],
            ].map(([label, value]) => (
              <li key={label} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid var(--border-1)", fontSize: 14 }}>
                <span style={{ color: "var(--fg-1)", fontWeight: 500 }}>{label}</span>
                <span style={{ color: "var(--fg-1)", fontWeight: 700 }}>{value}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* Achievements preview */}
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <span className="eyebrow">Badges · {unlocked.length} of {ACHIEVEMENTS.length}</span>
        </div>
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12 }}>
          {[...unlocked, ...locked].slice(0, 12).map((a) => {
            const isUnlocked = unlockedSet.has(a.id);
            return (
              <div key={a.id} title={a.desc} style={{ background: isUnlocked ? "var(--paper-deep)" : "transparent", border: `1px solid ${isUnlocked ? "var(--gold-300)" : "var(--ink-200)"}`, borderRadius: "var(--radius-md)", padding: 12, opacity: isUnlocked ? 1 : 0.4, textAlign: "center" }}>
                <div style={{ width: 32, height: 32, borderRadius: 999, background: isUnlocked ? "var(--gold-100)" : "var(--ink-100)", color: isUnlocked ? "var(--gold-700)" : "var(--ink-400)", display: "inline-flex", alignItems: "center", justifyContent: "center", margin: "0 0 6px" }}>
                  ★
                </div>
                <p style={{ fontSize: 11, fontWeight: 600, color: isUnlocked ? "var(--fg-1)" : "var(--fg-3)", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.title}</p>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function BigStat({ value, label }) {
  return (
    <div>
      <p className="font-display" style={{ fontSize: 56, lineHeight: 1, fontWeight: 400, color: "var(--ink-900)", margin: 0 }}>
        {value}
      </p>
      <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: "var(--fg-3)", margin: "6px 0 0" }}>
        {label}
      </p>
    </div>
  );
}
